import React from "react";
import { Authenticated, useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import { EmptyState } from "@libertify/ui";
import { TrendUpIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { SubPageHeader } from "components/SubPageHeader/";
import { StudioTopBar } from "components/layout/header/StudioTopBar";
import { PresetDateRangePicker } from "components/PresetDateRangePicker/";
import { ANALYTICS_MAX_DATE_RANGE_MONTHS } from "constants/";
import type { UseAnalyticsPageResult } from "../../projects/hooks/useAnalyticsPage";
import { AnalyticsTabs } from "./AnalyticsTabs";
import { AnalyticsVersionSelect } from "./AnalyticsVersionSelect";

export type AnalyticsSubPageLayoutProps = {
  authKey: string;
  translationPrefix: string;
  page: UseAnalyticsPageResult<unknown>;
  /** Shows the empty state with this title even when the summary has data. */
  emptyTitle?: string;
  children: React.ReactNode;
};

export const AnalyticsSubPageLayout: React.FC<AnalyticsSubPageLayoutProps> = ({
  authKey,
  translationPrefix,
  page,
  emptyTitle,
  children,
}) => {
  const t = useTranslate();
  const noData =
    (page.isSuccess || page.isError) &&
    !page.summaryData &&
    !page.isDataLoading;

  return (
    <Authenticated key={authKey}>
      <SubPageHeader
        itemId={page.projectId}
        itemTitle={page.project?.title ?? ""}
        page="analytics"
        source="projects"
        spaced
        onBack={page.handleBack}
        rightContent={<StudioTopBar />}
      />
      <Flex vertical gap={24}>
        <AnalyticsTabs />
        {page.dateFiltered && (
          <PresetDateRangePicker
            value={page.dateRange}
            onChange={page.setDateRange}
            dateFormat="L"
            minDate={dayjs().subtract(ANALYTICS_MAX_DATE_RANGE_MONTHS, "months")}
            disabled={page.isDataLoading}
          />
        )}

        <Flex wrap justify="space-between" align="center" gap={8}>
          <Typography.Title level={3} style={{ margin: 0, fontWeight: "normal" }}>
            {t(`${translationPrefix}.title`)}
          </Typography.Title>
          {/* On a failed list the version count is unknown: the select says so. */}
          {page.version?.hasChoice || page.version?.isError ? (
            <div style={{ width: 320, maxWidth: "100%" }}>
              <AnalyticsVersionSelect
                versions={page.version.versions}
                value={page.version.selected}
                onChange={page.version.setVersion}
                loading={page.version.isLoading}
                error={page.version.isError}
              />
            </div>
          ) : null}
        </Flex>

        {emptyTitle || noData ? (
          // Same empty state as the Overview tab.
          <Flex justify="center" align="center" style={{ marginTop: 100 }}>
            <EmptyState
              icon={<TrendUpIcon size={80} />}
              title={emptyTitle ?? t("media.projects.analytics.dashboard.noData")}
              // The hint talks about the selected period: only for date-filtered pages.
              description={
                !emptyTitle && page.dateFiltered
                  ? t("media.projects.analytics.dashboard.noDataHint")
                  : undefined
              }
            />
          </Flex>
        ) : null}

        {!emptyTitle && (page.summaryData || page.isDataLoading) && (
          <>{children}</>
        )}
      </Flex>
    </Authenticated>
  );
};
